import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { useAuth } from '@/features/auth/session';
import { AppLoader } from '@/shared/components/feedback/AppLoader';
import { SessionUnavailable } from '@/shared/components/feedback/SessionUnavailable';

/**
 * Keeps a signed-out visitor off the signed-in routes.
 *
 * <h2>This is convenience, not security</h2>
 *
 * It checks only that a token exists — not that it is valid, unexpired or
 * carries any authority. It cannot: the token is opaque and only the server can
 * say. Anyone can put a string in storage and get past this.
 *
 * <p>That is fine, because it is not what protects anything. Every endpoint
 * refuses an unauthenticated caller on its own, and a person who forces their way
 * to a screen finds it empty. What this buys is that somebody arriving with no
 * session sees the sign-in page instead of a dashboard flashing up and then
 * filling with errors.
 *
 * <h2>Where they came from is remembered</h2>
 *
 * The path is passed to sign-in so that signing in returns them to the page they
 * asked for, rather than dropping them on the dashboard to navigate again.
 */
/*
 * IT ASKS THE SAME QUESTION `GuestRoute` ASKS, and until 28 September 2026 it did not.
 *
 * This read the token out of storage; `GuestRoute` waited for `/account/me`. Those are
 * different questions, and a browser holding a token the server had rejected answered yes to
 * one and no to the other -- so the dashboard rendered AND `/login` rendered, in the same
 * browser, minutes apart, which is exactly what was reported. Both now read `status`.
 *
 * <p>The cost is one wait: a protected screen no longer paints on the strength of a token in
 * storage, it paints once the server has confirmed it. That request is made on every load
 * anyway -- the layout has the person's name in it -- so what changes is the order, not the
 * work.
 */
export function ProtectedRoute() {
  const location = useLocation();
  const { status, sessionUnreachable, retrySession } = useAuth();

  if (sessionUnreachable) {
    /*
     * The session cannot be resolved because the server cannot be asked -- it is down, or
     * unreachable, or answering 5xx. Not a refusal, so the token stays and nobody is signed
     * out over a backend restart.
     *
     * Said rather than spun on. `status` is 'unknown' here and will stay 'unknown', so the
     * loader below would run until the page was reloaded by hand -- a blank screen with a
     * spinner on it, which is what this looked like and gives somebody nothing to do.
     */
    return <SessionUnavailable onRetry={retrySession} />;
  }

  if (status === 'unknown') {
    return <AppLoader />;
  }

  if (status === 'signed-out') {
    return <Navigate to={ROUTES.signIn} state={{ from: location.pathname }} replace />;
  }

  return <Outlet />;
}
